import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import {
  ClientTransactions,
  Company,
  ExtractionSummary
} from "@/jenesys-types/jenesys-entities/config";

export interface CompanyDashboardState {
  activeCompanyId: string;
  activeCompany?: Company;
  dashboard: ExtractionSummary;
  totalDashboard: ExtractionSummary;
  isDashboardLoading: boolean;
  isTotalDashboardLoading: boolean;
  dashboardError?: AppError;
  companyTransactions?: ClientTransactions;
  isCompanyTransactionsLoading: boolean;
}

interface LoadCompanyDashboardPayload {
  companyId?: Id;
  dateRange?: any;
  offset?: number;
}
export type LoadCompanyDashboardAction =
  PayloadAction<LoadCompanyDashboardPayload>;

interface CompanyDashboardFailurePayload {
  error?: AppError;
  companyId?: string;
}
export type CompanyDashboardFailureAction =
  PayloadAction<CompanyDashboardFailurePayload>;

interface CompanyDashboardSuccessPayload {
  dashboard: ExtractionSummary;
}
export type CompanyDashboardSuccessAction =
  PayloadAction<CompanyDashboardSuccessPayload>;

interface CompanyTransactionsSuccessPayload {
  companyTransactions: ClientTransactions;
}
export type CompanyTransactionsSuccessAction =
  PayloadAction<CompanyTransactionsSuccessPayload>;

const name = "state/features/CompanyDashboard";

export const initialState: CompanyDashboardState = {
  activeCompanyId: "",
  activeCompany: undefined,
  isDashboardLoading: false,
  isTotalDashboardLoading: false,
  dashboardError: undefined,
  dashboard: {} as ExtractionSummary,
  totalDashboard: {} as ExtractionSummary,
  companyTransactions: {} as ClientTransactions,
  isCompanyTransactionsLoading: false
};

const CompanyDashboardSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadCompanyDashboard: {
      reducer: (
        state: CompanyDashboardState,
        _action: LoadCompanyDashboardAction
      ) => {
        state.isDashboardLoading = true;
      },
      prepare: (companyId: Id, dateRange: any) => ({
        payload: { companyId, dateRange }
      })
    },
    companyDashboardFailure: {
      reducer: (
        state: CompanyDashboardState,
        action: CompanyDashboardFailureAction
      ) => {
        state.dashboardError = action.payload.error;
        state.isDashboardLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    companyDashboardSuccess: {
      reducer: (
        state: CompanyDashboardState,
        action: CompanyDashboardSuccessAction
      ) => {
        state.isDashboardLoading = false;
        state.dashboard = action.payload.dashboard;
      },
      prepare: (dashboard: ExtractionSummary) => ({
        payload: { dashboard }
      })
    },
    loadCompanyTotalDashboard: {
      reducer: (
        state: CompanyDashboardState,
        _action: LoadCompanyDashboardAction
      ) => {
        state.isTotalDashboardLoading = true;
      },
      prepare: (companyId: Id) => ({
        payload: { companyId }
      })
    },
    companyTotalDashboardFailure: {
      reducer: (
        state: CompanyDashboardState,
        action: CompanyDashboardFailureAction
      ) => {
        state.dashboardError = action.payload.error;
        state.isTotalDashboardLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    companyTotalDashboardSuccess: {
      reducer: (
        state: CompanyDashboardState,
        action: CompanyDashboardSuccessAction
      ) => {
        state.isTotalDashboardLoading = false;
        state.totalDashboard = action.payload.dashboard;
      },
      prepare: (dashboard: ExtractionSummary) => ({
        payload: { dashboard }
      })
    },
    loadCompanyTransactions: {
      reducer: (
        state: CompanyDashboardState,
        _action: LoadCompanyDashboardAction
      ) => {
        state.isCompanyTransactionsLoading = true;
      },
      prepare: (companyId: Id, dateRange: any, offset: number) => ({
        payload: { companyId, dateRange, offset }
      })
    },
    companyTransactionsFailure: {
      reducer: (
        state: CompanyDashboardState,
        action: CompanyDashboardFailureAction
      ) => {
        state.dashboardError = action.payload.error;
        state.isCompanyTransactionsLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    companyTransactionsSuccess: {
      reducer: (
        state: CompanyDashboardState,
        action: CompanyTransactionsSuccessAction
      ) => {
        state.isCompanyTransactionsLoading = false;
        state.companyTransactions = action.payload.companyTransactions;
      },
      prepare: (companyTransactions: ClientTransactions) => ({
        payload: { companyTransactions }
      })
    }
  }
});

export const {
  companyDashboardSuccess,
  companyDashboardFailure,
  loadCompanyDashboard,
  companyTotalDashboardSuccess,
  companyTotalDashboardFailure,
  loadCompanyTotalDashboard,
  loadCompanyTransactions,
  companyTransactionsSuccess,
  companyTransactionsFailure
} = CompanyDashboardSlice.actions;

// Selectors
export const selectCompanyDashboardState = (state: {
  features: { CompanyDashboard: CompanyDashboardState };
}): CompanyDashboardState => state.features.CompanyDashboard;

export const selectDashboard = createSelector(
  selectCompanyDashboardState,
  (state) => state.dashboard
);
export const selectIsDashboardLoading = createSelector(
  selectCompanyDashboardState,
  (state) => state.isDashboardLoading
);

export default CompanyDashboardSlice.reducer;
